// src/shared/auth/rbac.js
//
// RBAC helpers use roleCatalog from the server (login + /api/auth/me), not static role constants.
// catalog.keyMap maps ROLE_KEYS ('INSTRUCTOR') → wire values ('Instructor') so callers
// can pass either and get correct level comparisons.

/**
 * Resolves a ROLE_KEY or wire value to its canonical wire value using catalog.keyMap.
 * Falls back to the input unchanged if not found.
 * @param {string} [role]
 * @param {{ legacyAliases?: Record<string,string>, keyMap?: Record<string,string> } | null} [catalog]
 */
export function normalizeRole(role, catalog) {
  if (!role) return role;
  // Resolve ROLE_KEY → wire value first (e.g. 'INSTRUCTOR' → 'Instructor')
  const wire = catalog?.keyMap?.[role];
  if (wire) return wire;
  // Then apply legacyAliases for wire value normalization
  const aliases = catalog?.legacyAliases;
  if (!aliases) return role;
  return aliases[role] || role;
}

/**
 * @param {string} userRole
 * @param {string} requiredRole  — accepts either a ROLE_KEY ('INSTRUCTOR') or wire value ('Instructor')
 * @param {{ levels?: Record<string,number>, keyMap?: Record<string,string>, legacyAliases?: Record<string,string> } | null} [catalog]
 */
export function hasMinRole(userRole, requiredRole, catalog) {
  if (!catalog?.levels) return false;
  const userLevel = catalog.levels[normalizeRole(userRole, catalog)];
  const requiredLevel = catalog.levels[normalizeRole(requiredRole, catalog)];
  if (userLevel === undefined || requiredLevel === undefined) return false;
  return userLevel >= requiredLevel;
}

/**
 * @param {string} userRole
 * @param {{ levels?: Record<string,number>, keyMap?: Record<string,string>, legacyAliases?: Record<string,string> } | null} catalog
 * @param {...string} roles — accepts ROLE_KEYS or wire values
 */
export function hasRole(userRole, catalog, ...roles) {
  const normalizedUser = normalizeRole(userRole, catalog);
  return roles.map((r) => normalizeRole(r, catalog)).includes(normalizedUser);
}

const ADMIN_LEVEL = 3;

/**
 * @param {boolean} aiAdmin
 * @param {string} userRole
 * @param {{ levels?: Record<string,number>, keyMap?: Record<string,string>, legacyAliases?: Record<string,string> } | null} catalog
 */
export function hasAdminAccess(aiAdmin, userRole, catalog) {
  if (aiAdmin === true) return true;
  if (!catalog?.levels) return false;
  const level = catalog.levels[normalizeRole(userRole, catalog)];
  return level !== undefined && level >= ADMIN_LEVEL;
}
